import { useContext, useEffect } from 'react';

import { chain } from 'lodash';

import { ApplicationContext } from '@src/app/contexts/Application';
import { OfferContext } from '@src/app/contexts/Offer';
import { useCallBookingVariant } from '@src/app/hooks/useCallBookingVariant';
import {
    Offer,
    Product,
    RecommendedOffer,
    SelectedOffer,
} from '@src/app/interfaces/offer';
import { ExperimentsConfig } from '@src/app/utils/experiments';
import { getProductType } from '@src/shared/helpers/offer';
import EligibilityLayout from '@src/ui/layouts/EligibilityLayout';
import ExlporeView from '@src/ui/views/offer/ExploreView';

export interface MarkedOffer {
    label: string;
    code: string;
}

const Explore = () => {
    const { offers, recommendedOffers, applicationId, hasDeclinedOffer } =
        useContext(ApplicationContext);

    const {
        loading,
        selectedOffer,
        setSelectedOffer,
        fetchOffers,
        offerData,
        fetchRecommendedOffers,
    } = useContext(OfferContext);
    const callBookingVariant = useCallBookingVariant();
    const isVariantB = callBookingVariant === ExperimentsConfig.Variants.B;

    useEffect(() => {
        const getOffers = async () => {
            if (!offerData || applicationId !== offerData.applicationId) {
                await fetchOffers();
            }
        };

        const getRecommendedOffers = async () => {
            if (!recommendedOffers) {
                await fetchRecommendedOffers();
            }
        };

        const getData = async () => {
            hasDeclinedOffer ? await getOffers() : await getRecommendedOffers();
        };

        getData();
    }, [
        fetchRecommendedOffers,
        applicationId,
        fetchOffers,
        hasDeclinedOffer,
        offerData,
        recommendedOffers,
    ]);

    const getRecommendedOffers = (): Offer[] => {
        const codes: string[] = recommendedOffers?.recommendedOffers
            .sort((a: any, b: any) => a.rank - b.rank)
            .map((offer: any) => offer.code);

        return chain(codes)
            .map((it) => offers.find((el: Offer) => it === el.productCode))
            .compact()
            .value();
    };

    const getOffers = (): Offer[] => {
        const ids = offerData?.offers.map((offer: SelectedOffer) => offer.id);

        const selectedOffers = offers.filter((offer: Offer) =>
            ids?.includes(offer.id),
        );

        return selectedOffers.sort((a: Offer, b: Offer) => {
            const aType = getProductType(a);
            const bType = getProductType(b);

            const productOrder = [
                Product.FiveYearFixedWithEarlyCharges,
                Product.TwoYearFixedWithEarlyCharges,
                Product.HELOC,
            ];

            return (
                productOrder.indexOf(aType as Product) -
                productOrder.indexOf(bType as Product)
            );
        });
    };

    const getSortedOffers: Offer[] = recommendedOffers
        ? getRecommendedOffers()
        : getOffers();

    if (!callBookingVariant) return null;

    const getMarkedOffer = (): MarkedOffer => {
        const popularProductCode = getSortedOffers.find((offer) => {
            const productType = getProductType(offer);

            return productType === Product.FiveYearFixedWithEarlyCharges;
        })?.productCode;

        const tailoredToPreferenceProductCode =
            recommendedOffers?.recommendedOffers.find(
                (offer: RecommendedOffer) => offer.selected,
            ).code;

        return recommendedOffers
            ? {
                  code: tailoredToPreferenceProductCode,
                  label: 'Matches your needs',
              }
            : {
                  code: popularProductCode,
                  label: 'Popular',
              };
    };

    return (
        <EligibilityLayout loading={loading} showTrustbarV2>
            <ExlporeView
                markedOffer={getMarkedOffer()}
                offers={getSortedOffers}
                selectedOffer={selectedOffer}
                onSelect={setSelectedOffer}
                hasDeclinedOffer={hasDeclinedOffer}
                isVariantB={isVariantB}
                callBookingVariant={callBookingVariant}
                applicationId={applicationId}
            />
        </EligibilityLayout>
    );
};

export default Explore;
